// One frame of the part rig, applied to a bundle's branches.
//
// The part drive poses a skeleton; a branch takes the result in one of THREE ways, and
// which one is a property of the branch, not a setting:
//
//   * it has a PART in rig.glb — its verts ARE the skinned result (body, clothes).
//   * it is LIVE (its own deform answers the face rig) — its verts are the deform's, so
//     the body's motion reaches it as a rigid delta on its ANCHOR instead. That route is
//     the only one that survives the GPU deform path, where the posed verts go straight
//     to the lifter as a GPUBuffer and `branch.verts` never leaves its neutral (so
//     writing them here would move nothing and read as a dead head). The caller re-poses
//     the branch afterwards, which is what makes the new anchor take effect.
//   * neither — the HAIR, whose groom export carries the trained poses and no skinning.
//     It rides the head bone rigidly, exactly as the neutral pipeline's phase 3 does.
//
// A branch reached by none of the three holds still and is REPORTED, because a region
// that quietly stays at neutral through a walk reads as a broken drive.

import { rigidBranchVerts } from './rigidVertices.js'

/** THREE's column-major `elements` → the row-vector `v·lin + off` pair the branch
 *  anchor is written in (poseAnchors.js's similarity algebra, vert_transform.wgsl).
 *  Transposing this looks like a slightly poor fit rather than like a bug. */
export function matrixToSimilarity(matrix) {
  const m = matrix?.elements ?? matrix
  return {
    lin: [m[0], m[1], m[2], m[4], m[5], m[6], m[8], m[9], m[10]],
    offset: [m[12], m[13], m[14]],
  }
}

/**
 * Apply the CURRENT part-rig pose to every branch.
 *
 * @param {object} o
 *   branches   OGSBranch[] (each with `name`, `verts`, `lifter`, `neutralVertices`)
 *   verts      the part driver's vertex scatter — `deform(name)` or null per branch
 *   headDelta  the head bone's rigid delta since rest, in the branch frame, or null
 *   rigidVerts Map name → own Float32Array for the rigid riders (never the mesh's own)
 *   isLive     (branch) => true when the branch's deform owns its verts
 *   skinners   Map name → GpuPartSkinner for the branches skinned ON DEVICE. Those never
 *              touch `branch.verts` (the CPU never sees the pose), exactly as the GPU
 *              deform path leaves it at the neutral.
 * @returns {{posed: string[], gpu: string[], anchored: string[], rigid: string[], still: string[]}}
 */
export function applyPartRigPose({ branches, verts, headDelta, rigidVerts, isLive = () => false, skinners = null, at = null }) {
  const report = { posed: [], gpu: [], anchored: [], rigid: [], still: [] }
  // WHEN each branch's verts were last set. The head lagging the body is a claim
  // about TIME, and nothing in this pipeline recorded any, so it could only be
  // argued from reading the code — which is how two wrong fixes got shipped
  // today. `__OGS_PIPELINE__.posedAt` makes the claim measurable: during a walk
  // every branch should carry the SAME clip time, and whichever one does not is
  // the bug. Cheap enough for the hot path (one property write per branch).
  const stamp = (br) => { if (at != null) br.lastPosedAt = at }
  const delta = headDelta ? matrixToSimilarity(headDelta) : null
  for (const br of branches) {
    if (isLive(br)) {
      // Skinned by the body like any other part, when it has one.
      //
      // WHO RUNS THE SKIN DEPENDS ON WHETHER THE BRANCH RE-POSES ITSELF, and
      // getting this wrong detaches the head completely. A branch with its own
      // deform runs `bodySkin` from `OGSBranch.pose()`, at the bone matrices just
      // set — so calling it here as well would skin it twice. A branch with NO
      // deform never reaches that line (`pose()` returns on `!this.deform`), so
      // nothing would ever run the skin: the head would take neither the skin nor
      // the anchor delta below and would sit perfectly still while the body walked
      // out from under it. That is not the old collar bug, it is worse, and it is
      // exactly what shipping the `continue` unconditionally produced on a
      // synthetic-face bundle (myra-pbr: `rig: none`, so no branch has a deform).
      if (br.bodySkin) {
        if (!br.hasDeform) br.bodySkin()
        stamp(br)
        report.gpu.push(br.name)
        continue
      }
      // The face keeps its expression and travels with the body.
      if (delta && br.setPoseAnchorDelta?.(delta.lin, delta.offset)) { stamp(br); report.anchored.push(br.name) }
      else report.still.push(br.name)
      continue
    }
    const skinner = skinners?.get(br.name)
    if (skinner) {
      // Skin → jacobians without a byte crossing the boundary: the lifter reads the
      // skinner's own buffer (GpuLifter.setVertsGpu).
      br.lifter.setVertsGpu(skinner.skin())
      stamp(br)
      report.gpu.push(br.name)
      continue
    }
    const posed = verts?.deform?.(br.name)
    if (posed) {
      br.verts = posed
      br.lifter?.computeJacobians(br.verts)
      stamp(br)
      report.posed.push(br.name)
      continue
    }
    if (headDelta && rigidBranchVerts(br, headDelta, rigidVerts)) {
      br.lifter?.computeJacobians(br.verts)
      stamp(br)
      report.rigid.push(br.name)
      continue
    }
    report.still.push(br.name)
  }
  return report
}

/** One-line summary of what the drive reaches, for the ready log. */
export function formatPartRigPose(report) {
  const part = (label, names) => (names.length ? `${label}: ${names.join(', ')}` : null)
  return [
    part('skinned(gpu)', report.gpu),
    part('skinned(cpu)', report.posed),
    part('anchored', report.anchored),
    part('rigid', report.rigid),
    part('STILL', report.still),
  ].filter(Boolean).join('; ')
}
